import React, { useState, useEffect } from 'react';
import { Layout, message } from 'antd';
import { authService } from './services/authService';
import Login from './pages/Login';
import Students from './pages/Students';
import Exams from './pages/Exams';
import Statistics from './pages/Statistics';
import UserManagement from './pages/UserManagement';
import SideNav from './components/SideNav';
import UserInfo from './components/UserInfo';
import Verification from './pages/Verification';
import ExamHistory from './pages/Exams/ExamHistory';
import ExamSummary from './pages/Exams/ExamSummary';
import ExamBooking from './pages/Exams/ExamBooking';
import StudyProgress from './pages/Exams/StudyProgress';
import Expenses from './pages/Expenses';
import './App.css';

const { Header, Content, Footer, Sider } = Layout;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState('students');

  useEffect(() => {
    const init = async () => {
      try {
        // Check if token is expired
        if (authService.isTokenExpired()) {
          setUser(null);
          return;
        }

        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          await authService.ensureRolesExist();
          const roles = await authService.getUserRoles(currentUser);
          currentUser.roles = roles;
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Initialization error:', error);
        message.error('系统初始化失败');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    init();

    // Set up interval to check token expiration
    const tokenCheckInterval = setInterval(() => {
      if (authService.isTokenExpired()) {
        setUser(null);
        message.info('登录已过期，请重新登录');
        clearInterval(tokenCheckInterval);
      }
    }, 60000); // Check every minute

    return () => clearInterval(tokenCheckInterval);
  }, []);

  const handleLoginSuccess = (user) => {
    setUser(user);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      message.success('退出登录成功');
    } catch (error) {
      message.error('退出登录失败: ' + error.message);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const renderContent = () => {
    switch (currentPage) {
      case 'students':
        return <Students />;
      case 'studentVerification':
        return <Verification type="student" />;
      case 'withdrawalVerification':
        return <Verification type="withdrawal" />;
      case 'documentation':
        return <Verification type="documentation" />;
      case 'exams':
        return <Exams />;
      case 'stats':
        return <Statistics />;
      case 'users':
        return <UserManagement />;
      case 'examBooking':
        return <ExamBooking onNavigate={setCurrentPage} />;
      case 'examHistory':
        return <ExamHistory />;
      case 'examSummary':
        return <ExamSummary />;
      case 'studyProgress':
        return <StudyProgress />;
      case 'expenses':
        return <Expenses />;
      default:
        return <div>404 Not Found</div>;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={(value) => setCollapsed(value)}
      >
        <SideNav 
          onMenuSelect={setCurrentPage} 
          collapsed={collapsed} 
        />
      </Sider>
      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: '#fff', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <h2 style={{ margin: 0 }}>汇丰驾校管理系统</h2>
          <UserInfo user={user} onLogout={handleLogout} />
        </Header>
        <Content style={{ margin: '16px' }}>
          <div style={{ padding: 24, minHeight: 360, background: '#fff' }}>
            {renderContent()}
          </div>
        </Content>
        <Footer style={{ textAlign: 'center' }}>
          汇丰驾校管理系统 | Huifeng Driver School Management System ©{new Date().getFullYear()} Created by Youming Ning
        </Footer>
      </Layout>
    </Layout>
  );
}

export default App;
